import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, mergeMap, tap, filter } from 'rxjs/operators';
import { of, from, EMPTY } from 'rxjs';
import { AuthService } from '../../../core/services/auth.service';
import { AuthActions } from './auth.actions';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { Action, Store } from '@ngrx/store';
import { ParentActions } from '../parent/parent.actions';
import { AlertService } from 'src/app/core/services/alert.service';
import { UserActions } from '../user/user.actions';
import { EncryptionService } from 'src/app/core/services/encryption.service';

@Injectable()
export class AuthEffects {
  constructor(
    private readonly actions$: Actions,
    private readonly router: Router,
    private readonly authService: AuthService,
    private readonly activatedRoute: ActivatedRoute,
    private readonly alertService: AlertService,
    private readonly store: Store,
    private readonly encryptionService: EncryptionService
  ) {}

  login$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.login),
      mergeMap((action) =>
        from(
          this.authService.login(
            action.username,
            action.password,
            action.captchaInput
          )
        ).pipe(
          map((response) => {
            if (response.message === 'CAPTCHA_VERIFICATION_FAILED') {
              this.alertService.error(response.message, 'Error');
              return AuthActions.generateCaptcha();
            }

            return AuthActions.loginSuccess({
              data: response.data,
              intendedUrl: action.intendedUrl ?? '',
            });
          }),
          // Filter out undefined results to avoid emitting them
          filter(
            (action: Action | undefined): action is Action =>
              action !== undefined
          ),
          catchError((error) => {
            console.log(`error.error?.data`, error.error?.data);
            if (error.error?.data?.isBlocked) {
              const redirectTo = error.error?.data.remainingLockTime
                ? '/account-locked-temp'
                : '/account-locked';
              const queryParams: Params | null | undefined = {};
              if (error.error?.message == "ACCOUNT_LOCKED_WITH_OTP_CONTACT_SUPPORT") {
                const o = this.encryptionService.encrypt(true);
                queryParams['o'] = o;
              }
              this.router.navigate([redirectTo], {
                queryParams,
              });
              return of(
                AuthActions.loginFailure({
                  error: error.error.message,
                  data: error.error?.data,
                })
              );
            }

            if (
              error.error.message ===
              'TRYING_TOO_MANY_WRONG_ATTEMPTS_CAPTCHA_REQUIRED'
            ) {
              return of(AuthActions.generateCaptcha());
            } else {
              return of(
                AuthActions.loginFailure({
                  error: error.error.message,
                  data: null,
                })
              );
            }
          })
        )
      )
    )
  );

  loginSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.loginSuccess),
      mergeMap((action) => {
        const parentId = action.data.user.parent_id;
        const actionsToDispatch = [];
        if (parentId && Number(parentId) !== 1) {
          // selectParent & permissions only for non-super admin users
          actionsToDispatch.push(
            ParentActions.selectParent({ id: parentId.toString() })
          );
          const userId = action.data.user.user_id;
          if (userId) {
            actionsToDispatch.push(
              UserActions.loadSingleUser({ id: userId, isUserPermission: true })
            );
          } else {
            // TBD: navigate to unauthorized page or show error
            this.router.navigate(['/unauthorized']);
          }
        } else {
          // if superadmin or no userId then remove permission from user
          actionsToDispatch.push(UserActions.clearUserPermissionSuccess());
        }
        return actionsToDispatch;
      })
    )
  );

  forgotPassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.forgotPassword),
      mergeMap((action) =>
        from(this.authService.forgotPassword(action.username)).pipe(
          map((response) => {
            this.alertService.success(response.message, 'Success');
            return AuthActions.forgotPasswordSuccess({
              username: action.username,
              withRedirect: action.withRedirect || false,
            });
          }),
          catchError((error) =>
            of(
              AuthActions.forgotPasswordFailure({ error: error.error.message })
            )
          )
        )
      )
    )
  );

  redirectAfterForgotPassword$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.forgotPasswordSuccess),
        tap(({ withRedirect }) => {
          if (!withRedirect) {
            this.router.navigate(['/login']);
          }
        })
      ),
    { dispatch: false }
  );

  resetPasswordLink$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.resetPasswordLink),
      mergeMap((action) =>
        from(this.authService.forgotPassword(action.username)).pipe(
          map((response) => {
            this.alertService.success(response.message, 'Success');
            return AuthActions.resetPasswordLinkSuccess({
              username: action.username,
            });
          }),
          catchError((error) =>
            of(
              AuthActions.resetPasswordLinkFailure({
                error: error.error.message,
              })
            )
          )
        )
      )
    )
  );

  redirectAfterLogin1$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.loginSuccess),
        tap((response) => {
          console.log(response, 'loginSuccess response');
          const intendedUrl = response?.intendedUrl || '/dashboard'; // <-- Get this from action or state
          if (response?.data?.token) {
            if (
              intendedUrl &&
              !intendedUrl.includes('login') &&
              response?.data?.user?.user_type !== 'super_admin'
            ) {
              this.router.navigate([intendedUrl]);
              return;
            }
            if (response?.data?.user?.user_type === 'super_admin') {
              this.router.navigate(['/business-unit']);
            }
          } else if (response?.data?.requiresOtp) {
            const qData = this.encryptionService.decrypt(this.activatedRoute.snapshot.queryParamMap.get('q'));
            qData.mfa = true;
            const q = this.encryptionService.encrypt(JSON.stringify(qData));
            this.router.navigate(['/login'], {
              queryParams: { q },
            });
          } else {
            this.router.navigate(['/login']);
          }
        })
      ),
    { dispatch: false }
  );

  redirectAfterCheckSession$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        AuthActions.isSessionExistSuccess,
        AuthActions.validateMagicOTPSuccess,
        AuthActions.validateMFAOTPSuccess
      ),
      mergeMap((response) => {
        if (!response.data.token) {
          this.router.navigate(['/login']);
          return EMPTY;
        }

        const parentId =
          'parent' in response.data
            ? response.data.parent?.toString()
            : response.data.user.parent_id;

        const actionsToDispatch: Array<Action> = [];
        if (parentId) {
          actionsToDispatch.push(ParentActions.selectParent({ id: parentId }));
        }
        const userId = response?.data?.user?.user_id;
        const isSuperAdmin = response?.data?.user?.user_type === 'super_admin';
        if (!isSuperAdmin && userId) {
          actionsToDispatch.push(
            UserActions.loadSingleUser({ id: userId, isUserPermission: true })
          );
        } else {
          actionsToDispatch.push(UserActions.clearUserPermissionSuccess());
        }

        // Deferred navigation: keep deep-link for session restore; OTP/MFA flows fallback to role landing.
        setTimeout(() => {
          const actionType = response.type;
          const intended = response.intendedUrl;
          const LANDING_SUPER_ADMIN = '/business-unit';
          const LANDING_USER = '/dashboard';

          switch (actionType) {
            case AuthActions.isSessionExistSuccess.type: {
              // Navigate only if intended differs (prevents duplicate navigation on refresh).
              if (intended && intended !== this.router.url) {
                this.router.navigateByUrl(intended);
              }
              break;
            }
            case AuthActions.validateMagicOTPSuccess.type:
            case AuthActions.validateMFAOTPSuccess.type: {
              const target = intended || (isSuperAdmin ? LANDING_SUPER_ADMIN : LANDING_USER);
              if (target !== this.router.url) {
                this.router.navigateByUrl(target);
              }
              break;
            }
            default:
              break; // No navigation for other actions here.
          }
        }, 0);

        return actionsToDispatch.length ? of(...actionsToDispatch) : EMPTY;
      })
    )
  );

  isSessionExist$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.isSessionExist),
      mergeMap((action) =>
        from(this.authService.isSessionExist()).pipe(
          map((response) =>
            AuthActions.isSessionExistSuccess({
              data: response.data,
              intendedUrl: action.intendedUrl,
            })
          ),
          catchError((error) =>
            of(AuthActions.isSessionExistFailure({ error: error.message }))
          )
        )
      )
    )
  );

  logout$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.logout),
      mergeMap(() =>
        from(this.authService.logout()).pipe(
          map(() => AuthActions.logoutSuccess({})),
          catchError((error) =>
            of(AuthActions.logoutFailure({ error: error.message }))
          )
        )
      )
    )
  );

  redirectAfterLogout$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.logoutSuccess),
        tap(() => {
          this.router.navigate(['/login']);
        })
      ),
    { dispatch: false }
  );

  generateCaptcha$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.generateCaptcha),
      mergeMap(() =>
        from(this.authService.generateCaptcha()).pipe(
          map((response) => {
            if (response?.status === false) {
              this.alertService.error(response.message, 'Error');
            }
            return AuthActions.generateCaptchaSuccess({
              data: response.data,
              message: response.message,
              status: response.status,
            });
          }),
          catchError((error) =>
            of(AuthActions.generateCaptchaFailure({ error: error.message }))
          )
        )
      )
    )
  );

  useMagicLink$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.useMagicLink),
      mergeMap((action) =>
        from(this.authService.useMagicLink(action.email)).pipe(
          map((response) => {
            console.log(response, 'useMagicLink response');
            this.alertService.success(response.message, 'Success');
            return AuthActions.useMagicLinkSuccess(response.data);
          }),
          catchError((error) => {
            if (error.error?.data?.isBlocked) {
              const redirectTo = error.error?.data.remainingLockTime
                ? '/account-locked-temp'
                : '/account-locked';
              const queryParams: Params | null | undefined = {};
              if (error.error?.message == "ACCOUNT_LOCKED_WITH_OTP_CONTACT_SUPPORT") {
                const o = this.encryptionService.encrypt(true);
                queryParams['o'] = o;
              }
              this.router.navigate([redirectTo], {
                queryParams,
              });
              return of(
                AuthActions.useMagicLinkFailure({
                  error: error.error.message,
                  data: error.error.data,
                })
              );
            }
            return of(
              AuthActions.useMagicLinkFailure({
                error: error.message,
                data: null,
              })
            );
          })
        )
      )
    )
  );

  validateMagicLink$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.validateMagicLink),
      mergeMap((action) =>
        from(this.authService.validateMagicLink(action.token)).pipe(
          mergeMap((response) => {
            const actionsToDispatch: Array<Action> = [];
            if (response.data.user.user_type === 'super_admin') {
              this.router.navigate(['/business-unit']);
            } else {
              if (response.data.user.parent_id) {
                actionsToDispatch.push(
                  ParentActions.selectParent({
                    id: response.data.user.parent_id?.toString() ?? '',
                  })
                );
              }
              actionsToDispatch.push(
                UserActions.loadSingleUser({
                  id: response.data.user.user_id ?? '',
                  isUserPermission: true,
                })
              );
              this.router.navigate(['/dashboard']);
            }
            actionsToDispatch.push(
              AuthActions.validateMagicLinkSuccess({
                data: response.data,
                intendedUrl: '/dashboard',
              })
            );
            return of(...actionsToDispatch);
          }),
          catchError((error) => {
            console.log(`error.error?.data`, error.error?.data);
            if (error.error?.data?.isBlocked) {
              const redirectTo = error.error?.data.remainingLockTime
                ? '/account-locked-temp'
                : '/account-locked';
              const queryParams: Params | null | undefined = {};
              if (error.error?.message == "ACCOUNT_LOCKED_WITH_OTP_CONTACT_SUPPORT") {
                const o = this.encryptionService.encrypt(true);
                queryParams['o'] = o;
              }
              this.router.navigate([redirectTo], {
                queryParams,
              });
              return of(
                AuthActions.validateMagicLinkFailure({
                  error: error.error.message,
                  data: error.error?.data,
                })
              );
            }
            this.router.navigate(['/login']);
            return of(
              AuthActions.validateMagicLinkFailure({
                error: error.message,
                data: null,
              })
            );
          })
        )
      )
    )
  );

  verifyResetToken$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.verifyResetToken),
      mergeMap((action) =>
        from(
          this.authService.validateResetToken(action.token, action.isResetToken)
        ).pipe(
          mergeMap((response) => {
            if (response.status) {
              return of(
                AuthActions.verifyResetTokenSuccess({ data: response.message })
              );
            } else {
              return of(
                AuthActions.verifyResetTokenFailure({
                  error: response.message,
                })
              );
            }
          }),
          catchError((error) => {
            return of(
              AuthActions.verifyResetTokenFailure({
                error: error.message,
              })
            );
          })
        )
      )
    )
  );

  tokenExpired$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          AuthActions.verifyResetTokenFailure,
          AuthActions.resetPasswordLinkFailure
        ),
        tap(() => {
          this.router.navigate(['/login'], {
            replaceUrl: true,
          });
        })
      ),
    {
      dispatch: false,
    }
  );

  forgotPasswordFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.forgotPasswordFailure),
        tap((response) => {
          if (response.error !== 'USER_INACTIVE') {
            this.router.navigate(['/account-locked']);
          }
        })
      ),
    {
      dispatch: false,
    }
  );

  validateMagicOTP$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.validateMagicOTP),
      mergeMap((action) =>
        from(this.authService.validateMagicOTP(action.email, action.otp)).pipe(
          map((response) => {
            return AuthActions.validateMagicOTPSuccess({
              data: response.data,
              intendedUrl: '/dashboard',
            });
          }),
          catchError((error) => {
            console.log(error, 'validateMagicOTP error');
            if (error.error?.data?.isBlocked) {
              const redirectTo = error.error?.data.remainingLockTime
                ? '/account-locked-temp'
                : '/account-locked';
              const queryParams: Params | null | undefined = {};
              if (error.error?.message == "ACCOUNT_LOCKED_WITH_OTP_CONTACT_SUPPORT") {
                const o = this.encryptionService.encrypt(true);
                queryParams['o'] = o;
              }
              this.router.navigate([redirectTo], {
                queryParams,
              });
              return of(
                AuthActions.validateMagicOTPFailure({
                  error: error.error.message,
                  data: error.error.data,
                })
              );
            }
            return of(
              AuthActions.validateMagicOTPFailure({
                error: error.message,
                data: null,
              })
            );
          })
        )
      )
    )
  );

  validateMFAOTP$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.validateMFAOTP),
      mergeMap((action) =>
        from(this.authService.validateMFAOTP(action.email, action.otp)).pipe(
          map((response) => {
            return AuthActions.validateMFAOTPSuccess({
              data: response.data,
              intendedUrl: '/dashboard',
            });
          }),
          catchError((error) => {
            console.log(error, 'validateMFAOTP error');
            if (error.error?.data?.isBlocked) {
              const redirectTo = error.error?.data.remainingLockTime
                ? '/account-locked-temp'
                : '/account-locked';
              const queryParams: Params | null | undefined = {};
              if (error.error?.message == "ACCOUNT_LOCKED_WITH_OTP_CONTACT_SUPPORT") {
                const o = this.encryptionService.encrypt(true);
                queryParams['o'] = o;
              }
              this.router.navigate([redirectTo], {
                queryParams,
              });
              return of(
                AuthActions.validateMFAOTPFailure({
                  error: error.error.message,
                  data: error.error.data,
                })
              );
            }
            return of(
              AuthActions.validateMFAOTPFailure({
                error: error.message,
                data: null,
              })
            );
          })
        )
      )
    )
  );

  resetPassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.resetPassword),
      mergeMap((action) =>
        from(
          this.authService.resetPassword(
            action.token,
            action.newPassword,
            action.confirmPassword
          )
        ).pipe(
          map((response) => {
            this.alertService.success(response.message, 'Success');
            this.router.navigate(['/login']);
            return AuthActions.resetPasswordSuccess({
              data: {},
            });
          }),
          catchError((error) =>
            of(AuthActions.resetPasswordFailure({ error: error.message }))
          )
        )
      )
    )
  );

  changePassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.changePassword),
      mergeMap((action) =>
        from(
          this.authService.changePassword(
            action.oldPassword,
            action.newPassword
          )
        ).pipe(
          map((response) => {
            this.alertService.success(response.message, 'Success');

            return AuthActions.changePasswordSuccess({
              data: {},
            });
          }),
          catchError((error) =>
            of(AuthActions.changePasswordFailure({ error: error.message }))
          )
        )
      )
    )
  );

  changePasswordSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.changePasswordSuccess),
        tap(() => {
          this.store.dispatch(AuthActions.logout({}));
        })
      ),
    {
      dispatch: false,
    }
  );

  resendMFAOTP$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.resendMFAOTP),
      mergeMap((action) =>
        from(this.authService.resendMFAOTP(action.email)).pipe(
          map((response) => {
            console.log(response, 'resendMFAOTP response');
            this.alertService.success(response.message, 'Success');
            return AuthActions.resendMFAOTPSuccess();
          }),
          catchError((error) => {
            return of(
              AuthActions.resendMFAOTPFailure({
                error: error.message,
                data: error.error.data,
              })
            );
          })
        )
      )
    )
  );
}
